import React from 'react';
import styled, { keyframes } from 'styled-components';
import Form from 'lib/components/Form';
import { FormikValues } from 'formik';
import { IField, ComponentType, SharedFormProps } from 'lib/components/Form/form.types';
import { Props as SubmissionMessageComponentProps } from 'lib/components/Form/SubmissionMessage';
import {
  Options,
  HubspotField,
  LegalConsentCommunication,
  isLegalConsentCommunication,
  isFormResponseFailure,
  isFormResponseSuccess,
} from './hubspot.types';


export interface Props {
  fieldDefinitions: IField[],
  formGuid: string,
  portalId: string,
  consentToProcessText?: string,
  context?: Options['context'],
  validationErrorComponent?: ComponentType,
  submitComponent?: ComponentType,
  preSubmitComponent?: ComponentType,
  fieldsContainerComponent?: ComponentType,
  submitContainerComponent?: ComponentType,
  labelComponent?: ComponentType,
  fieldWrapComponent?: ComponentType,
  fieldInnerWrapComponent?: ComponentType,
  submissionMessageComponent?: ComponentType<SubmissionMessageComponentProps>,
  onSubmitOverride?: (
    values: FormikValues,
    originalSubmitFunction: SharedFormProps['onSubmit'],
  ) => Promise<void>,
  formName?: string
}


function getCommunications(
  values: FormikValues,
  fieldDefinitions: IField[],
): LegalConsentCommunication[] {
  const gdprFieldDefinitions = fieldDefinitions.filter((definition) => (
    definition.hubspotSubscriptionTypeId !== 'undefined'
  ));

  const communicationObjectCandidates = gdprFieldDefinitions.map((definition) => {
    const correspondingValue: unknown = values[definition.slug];
    if (correspondingValue !== 'undefined') {
      return {
        subscriptionTypeId: definition.hubspotSubscriptionTypeId,
        value: correspondingValue,
        text: definition.label,
      };
    }
    return null;
  });

  return communicationObjectCandidates.filter(isLegalConsentCommunication);
}


function getFields(
  values: FormikValues,
  fieldDefinitions: IField[],
): HubspotField[] {
  const fieldDefinitionsWithoutGdpr = fieldDefinitions.filter((definition) => (
    typeof definition.hubspotSubscriptionTypeId === 'undefined'
  ));
  return fieldDefinitionsWithoutGdpr.map((definition) => ({
    name: definition.slug,
    value: values[definition.slug], // eslint-disable-line @typescript-eslint/no-unsafe-assignment
  }));
}


const HubspotForm:React.FC<Props> = ({
  fieldDefinitions,
  formGuid,
  portalId,
  consentToProcessText = 'By clicking submit below, you consent to allow blueclaw to store and process the personal information submitted above to provide you the content requested.',
  context,
  validationErrorComponent = ValidationErrorStyled,
  submitComponent = SubmitButton,
  preSubmitComponent = () => <PreSubmitComponent>{consentToProcessText}</PreSubmitComponent>,
  fieldsContainerComponent = FieldsContainer,
  submitContainerComponent = SubmitContainer,
  labelComponent = Label,
  fieldWrapComponent = FieldWrap,
  fieldInnerWrapComponent = FieldInnerWrap,
  submissionMessageComponent,
  onSubmitOverride,
  formName,
}) => {
  const handleSubmitDefault = async (values: FormikValues): Promise<any> => {
    const submissionData: Options = {
      formGuid,
      portalId,
      fields: getFields(values, fieldDefinitions),
      context,
      legalConsentOptions: {
        consent: {
          text: consentToProcessText,
          consentToProcess: true,
          communications: getCommunications(values, fieldDefinitions),
        },
      },
    };
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-cache',
      body: JSON.stringify(submissionData),
    };

    const requestUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formGuid}`;

    const res = await window.fetch(requestUrl, requestOptions);
    const json: unknown = await res.json();

    if (isFormResponseFailure(json)) {
      throw new Error([
        '\n',
        'Form submission error:',
        JSON.stringify(json, null, 2),
      ].join('\n'));
    } else if (isFormResponseSuccess(json)) {
      return json.inlineMessage;
    } else {
      throw new Error('error encountered');
    }
  };

  const handleSubmit: SharedFormProps['onSubmit'] = async (vals) => {
    if (typeof onSubmitOverride === 'function') await onSubmitOverride(vals, handleSubmitDefault);
    else await handleSubmitDefault(vals);
  };

  return (
    <FormWrapOuter>
      <Form
        fields={fieldDefinitions}
        onSubmit={handleSubmit}
        validationErrorComponent={validationErrorComponent}
        submitComponent={submitComponent}
        preSubmitComponent={preSubmitComponent}
        fieldsContainerComponent={fieldsContainerComponent}
        submitContainerComponent={submitContainerComponent}
        labelComponent={labelComponent}
        fieldWrapComponent={fieldWrapComponent}
        fieldInnerWrapComponent={fieldInnerWrapComponent}
        submissionMessageComponent={submissionMessageComponent}
        formName={formName}
      />
    </FormWrapOuter>
  );
};
export default HubspotForm;

const FormWrapOuter = styled.div``;
const ValidationErrorStyled = styled.span`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateX(100%) translateY(-50%);
  padding: 0 10px;
  color: red;
  animation: ${keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
  `} 0.3s both;
`;
const SubmitButton = styled.button``;
const FieldsContainer = styled.div``;
const SubmitContainer = styled.div`
  text-align: right;
`;
const PreSubmitComponent = styled.div`
  margin-bottom: 10px;
  font-size: 1rem;
  font-style: italic;
  text-align: right;
`;
const Label = styled.label``;
const FieldWrap = styled.div``;
const FieldInnerWrap = styled.div``;
